/**
 * The blood-splatter layer's **pure** state machine (Blood splatter, PRD #372,
 * Slice A): the transient dark-red decals a landed hit leaves on the ground. It is
 * the exact sibling of {@link ../render/combatText.CombatTextLayer} - a small set
 * of live items, each aged by the render clock (the same `dtMs` the renderer feeds
 * its sprites), kept free of Pixi, the DOM, wall clocks, and randomness so it is
 * node-testable in isolation. The renderer ({@link ./bloodLayer.BloodLayer})
 * consumes {@link BloodSplatField.active} each frame and reconciles a pooled decal
 * sprite per splat id.
 *
 * Each splat is pinned to a ground tile (already resolved from the victim tile plus
 * the server-rolled scatter offset by the layer). Over its {@link BLOOD_TTL_MS}
 * life it **pops in** fast (a ~{@link BLOOD_POP_MS} scale + alpha bloom, so the
 * splat lands with a snap rather than fading up), **holds** at full, then **fades
 * out** over the last {@link BLOOD_FADE_MS} of its life. A miss draws nothing - the
 * caller simply never spawns.
 *
 * Blood is render-state exactly like the combat floats: never an entity, never in
 * the snapshot, never persisted. A player logging in mid-splatter sees nothing,
 * which is correct for a 3-second decal.
 */

/** How long a splat lives before it has fully faded and should be dropped (ms). */
export const BLOOD_TTL_MS = 3000;

/** The opening slice over which a fresh splat pops in (scale + alpha bloom), in ms. */
export const BLOOD_POP_MS = 100;

/** The trailing slice of the life over which a splat fades from full to nothing (ms). */
export const BLOOD_FADE_MS = 500;

/** Scale a splat starts at before it pops to full size over {@link BLOOD_POP_MS}. */
export const BLOOD_POP_START_SCALE = 0.5;

/**
 * Ceiling on how many splats are live at once. A storm wipe or an arena cascade can
 * fling a great many hits in a heartbeat, so the display list is capped and the
 * oldest evicted rather than growing without bound (the overhead-speech / combat-
 * float precedent). 64 is generous for a busy bank but bounded.
 */
export const BLOOD_MAX = 64;

/** A splat's alpha at `ageMs`: blooms in over the pop, holds at 1, then fades out. */
export function bloodSplatAlpha(ageMs: number): number {
  if (ageMs < BLOOD_POP_MS) return Math.max(0, Math.min(1, ageMs / BLOOD_POP_MS));
  const remaining = BLOOD_TTL_MS - ageMs;
  if (remaining >= BLOOD_FADE_MS) return 1;
  return Math.max(0, Math.min(1, remaining / BLOOD_FADE_MS));
}

/** A splat's scale at `ageMs`: pops from {@link BLOOD_POP_START_SCALE} to 1, then holds. */
export function bloodSplatScale(ageMs: number): number {
  if (ageMs >= BLOOD_POP_MS) return 1;
  const t = Math.max(0, Math.min(1, ageMs / BLOOD_POP_MS));
  return BLOOD_POP_START_SCALE + (1 - BLOOD_POP_START_SCALE) * t;
}

/** Whether a splat has outlived its TTL and should be dropped. */
export function bloodSplatExpired(ageMs: number): boolean {
  return ageMs >= BLOOD_TTL_MS;
}

/** One live splat: where it sits, which art it draws, and its raw age. */
interface Splat {
  id: number;
  /** Resolved ground tile (victim tile + scatter offset), in tile coordinates. */
  tileX: number;
  tileY: number;
  /** Blood art index `0..6` (which of the 7 blood statics), driving texture + fallback. */
  art: number;
  /** Optional UO hue override (the `BloodHue` seam); absent = the art's natural red. */
  hue?: number;
  ageMs: number;
}

/** A splat as the renderer consumes it each frame: its id, placement, look, and motion. */
export interface ActiveSplat {
  id: number;
  tileX: number;
  tileY: number;
  art: number;
  hue?: number;
  /** `[0, 1]` opacity. */
  alpha: number;
  /** Uniform scale (the pop bloom, then 1). */
  scale: number;
}

/**
 * The live blood splats. The layer spawns into it on a `splat(...)` call, advances
 * it each frame, and reconciles its pooled decal sprites to {@link active}. Pure and
 * clock-free, the {@link ../render/combatText.CombatTextLayer} twin.
 */
export class BloodSplatField {
  private splats: Splat[] = [];
  private nextId = 1;

  /** How many splats are currently live (the renderer/tests read this). */
  get size(): number {
    return this.splats.length;
  }

  /**
   * Spawn a fresh splat on tile `(tileX, tileY)` with the given art index (and
   * optional hue) and return its `id`. Over the {@link BLOOD_MAX} cap the oldest is
   * evicted; its id is returned so the caller can destroy that decal's sprite (the
   * combat-float eviction contract).
   */
  spawn(tileX: number, tileY: number, art: number, hue?: number): { id: number; evicted: number[] } {
    const id = this.nextId++;
    this.splats.push({ id, tileX, tileY, art, hue, ageMs: 0 });
    const evicted: number[] = [];
    while (this.splats.length > BLOOD_MAX) evicted.push(this.splats.shift()!.id);
    return { id, evicted };
  }

  /**
   * Age every splat by `dtMs` and drop those past their TTL. Returns the ids that
   * left this tick so the renderer can destroy their decal sprites. Pure over the
   * list.
   */
  advance(dtMs: number): number[] {
    const removed: number[] = [];
    this.splats = this.splats.filter((s) => {
      s.ageMs += dtMs;
      if (bloodSplatExpired(s.ageMs)) {
        removed.push(s.id);
        return false;
      }
      return true;
    });
    return removed;
  }

  /** The live splats with their derived alpha/scale, oldest first. */
  active(): ActiveSplat[] {
    return this.splats.map((s) => ({
      id: s.id,
      tileX: s.tileX,
      tileY: s.tileY,
      art: s.art,
      hue: s.hue,
      alpha: bloodSplatAlpha(s.ageMs),
      scale: bloodSplatScale(s.ageMs),
    }));
  }
}
